import React, { useState } from 'react';
import { hot } from 'react-hot-loader';
import Link from 'redux-first-router-link';
import ErrorBoundary from '../../hocs/ErrorBoundary/ErrorBoundary';
import Slider from '../../hocs/Slider/Slider';
import BackgroundMedia from './BackgroundMedia/BackgroundMedia';
import Button from './Button/Button';
import Dropbox from './Dropbox/Dropbox';
import Embed from './Embed/Embed';
import Heading from './Heading/Heading';
import Image from './Image/Image';
import InlineCategoryContent from './InlineCategoryContent/InlineCategoryContent';
import List from './List/List';
import Paragraph from './Paragraph/Paragraph';
import Placeholder from './Placeholder/Placeholder';
import Products from './Products/Products';
import Separator from './Separator/Separator';
import Spacer from './Spacer/Spacer';
import SectionTitle from './SectionTitle/SectionTitle';
import Stockists from './Stockists/Stockists';
import Table from './Table/Table';
import Video from './Video/Video';
import VideoEmbed from './VideoEmbed/VideoEmbed';
import { percentToColumn } from '../../utils/columns';
import { purifier } from '../../utils/dom';
import { deepMerge } from '../../utils/object';
import { titleCase, urlify, camelCase, cleanInternalLinks } from '../../utils/string';
import { isRestricted } from '../../utils/restrict';
import s from './TemplateParser.scss';

const components = {
	acfInlineCategoryContent: InlineCategoryContent,
	acfDropboxFolder: Dropbox,
	acfPlaceholder: Placeholder,
	acfProducts: Products,
	acfStockists: Stockists,
	coreButton: Button,
	coreEmbed: VideoEmbed,
	coreHeading: Heading,
	coreHtml: Embed,
	coreImage: Image,
	coreParagraph: Paragraph,
	coreSeparator: Separator,
	coreSpacer: Spacer,
	coreVideo: Video,
	coreTable: Table,
};

const containers = {};

const heightSettings = ({ attrs = {}, setup }) => {
	const extraProps = {};
	const settings = {};

	if ((attrs.fixedHeightValue && setup.breakPoint === 'desktop')
		|| (attrs.fixedHeightValueMd && setup.breakPoint === 'tablet')
		|| (attrs.fixedHeightValueSm && setup.breakPoint === 'mobile')
	) {
		extraProps.className = ['u_fillContainer'];
		extraProps.style = {
			overflow: 'hidden',
		};
		settings.fill = true;
		settings.focalPoint = attrs.focalPoint;
	}

	if (attrs.fixedHeightValue && setup.breakPoint === 'desktop') extraProps.style.height = `${attrs.fixedHeightValue}vh`;
	if (attrs.fixedHeightValueMd && setup.breakPoint === 'tablet') extraProps.style.height = `${attrs.fixedHeightValueMd}vh`;
	if (attrs.fixedHeightValueSm && setup.breakPoint === 'mobile') extraProps.style.height = `${attrs.fixedHeightValueSm}vh`;

	return [extraProps, settings];
};

const generalSettings = ({ customProps, attrs = {}, template }) => {
	const attrsClassName = attrs.className || [];

	const generalProps = {
		className: [
			s.container,
			s[`container__${template}`],
			...attrsClassName.map((cN) => s[`container${cN}`]),
		],
		style: {},
	};

	// Colors
	if (attrs.backgroundColor) generalProps.style.backgroundColor = attrs.backgroundColor;
	if (attrs.textColor) {
		generalProps.style.color = attrs.textColor;
		generalProps.className.push('u_white');
	}

	// Alignment
	if (attrs.align && attrs.align !== 'full') generalProps.style.textAlign = attrs.align;
	if (attrs?.layout?.justifyContent && attrs.layout.justifyContent !== 'full') generalProps.style.justifyContent = attrs.layout.justifyContent;
	if (attrs.verticalAlignment) generalProps.className[generalProps.className.length] = s[`container__v${titleCase(attrs.verticalAlignment)}`];
	if (attrs.align === 'full') generalProps.className[generalProps.className.length] = s.container__alignFull;

	const extraProps = deepMerge(generalProps, customProps);
	extraProps.className = extraProps.className.join(' ');

	return extraProps;
};

const getElement = (attrs, setup) => {
	const extraProps = {};
	let Element = 'div';

	if (attrs.href) {
		const link = cleanInternalLinks(attrs.href, setup);
		const externalLink = /^(https?|mailto|tel)/.test(link);
		const anchorLink = /^#/.test(link);

		if (attrs.target || externalLink || anchorLink) {
			Element = 'a';
			extraProps.href = link;
			extraProps.target = '_blank';
			extraProps.rel = 'noopener noreferrer';
		} else {
			Element = Link;
			extraProps.to = link.replace(/\/+$/, '');
		}
	}

	return [Element, extraProps];
};

const getTemplate = (template, attrs, setup, useComponents = true) => {
	if (isRestricted(setup, attrs)) return null;
	if (components[template]) {
		if (useComponents) return components[template];
		return NoContainer;
	}
	if (containers[template]) return containers[template];

	if (['ccBlocksMeta', 'acfSectionTitle'].indexOf(template) === -1) console.log('TEMPLATE MISSING: ', template);
	return null;
};

containers.acfCategory = ({ content = [], setup, inlineCategoryContent }) => {
	if (!Array.isArray(content)) content = [{ content: { inlineMedia: content.inlineMedia }, key: content.key }];
	return (
		<>
			{
				content.map((childSection) => {
					let tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					if (tpl === 'content') tpl = 'acfInlineCategoryContent';
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) {
						console.log('TEMPLATE MISSING FROM: ', nextContent);
						return null;
					}
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} inlineCategoryContent={inlineCategoryContent} {...nextContent} />;
				})
			}
		</>
	);
};

containers.ccBlocksAccordion = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const customProps = {};

	const extraProps = generalSettings({ customProps, attrs, template });

	return (
		<div {...extraProps}>
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) {
						console.log('TEMPLATE MISSING FROM: ', nextContent);
						return null;
					}
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
				})
			}
		</div>
	);
};

containers.ccBlocksAccordionItem = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const customProps = {};
	const accordionKey = urlify(attrs.title.slice(0, 15));

	const [accordianToggled, toggleAccordian] = useState(props.hashLocation === accordionKey);
	const toggleAccordianHandler = (e) => {
		e.preventDefault();
		toggleAccordian((prevState) => !prevState);
	};

	const extraProps = generalSettings({ customProps, attrs, template });

	return (
		<div {...extraProps}>
			<button aria-controls={accordionKey} aria-expanded={accordianToggled} type="button" onClick={toggleAccordianHandler} className={[s.container_title, (accordianToggled ? s.container_title__expanded : null)].join(' ')}>
				<span>{ attrs.title }</span>
			</button>
			<div aria-hidden={!accordianToggled} id={accordionKey} className={[s.container_content, (accordianToggled ? s.container_content__expanded : null)].join(' ')}>
				{
					content.map((childSection) => {
						const tpl = Object.keys(childSection)[0];
						const nextContent = childSection[tpl];
						const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
						if (!ChildElement) {
							console.log('TEMPLATE MISSING FROM: ', nextContent);
							return null;
						}
						return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
					})
				}
			</div>
		</div>
	);
};

containers.ccBlocksPuff = ({ attrs = {}, content = [], uid, setup, template, ...props }) => {
	const [customProps, settings] = heightSettings({ attrs, setup });

	const [Element, elementProps] = getElement(attrs, setup);
	customProps.className = [s[`container__${camelCase(attrs.layout || 'center Center')}`]];
	const extraProps = generalSettings({ customProps: deepMerge(customProps, elementProps), attrs, template });

	return (
		<Element {...extraProps}>
			<BackgroundMedia content={attrs} uid={uid} {...settings} {...props} />
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) {
						console.log('TEMPLATE MISSING FROM: ', nextContent);
						return null;
					}
					return <ChildElement template={tpl} key={nextContent.key} isPuffContent setup={setup} {...nextContent} {...props} />;
				})
			}
		</Element>
	);
};
containers.ccBlocksPuffAlt = containers.ccBlocksPuff;

const scrollRight = (e) => {
	e.preventDefault();
	e.currentTarget.nextSibling.scrollLeft += (window.innerWidth * 0.7) + 5;
};

containers.ccBlocksSlider = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const [customProps] = heightSettings({ attrs, setup });
	const columnSlider = content.filter((section) => Object.keys(section)[0] === 'coreColumns').reduce((acc, next) => {
		const col = next.coreColumns.content.length;
		if (col > acc) return col;
		return acc;
	}, 0) || false;

	const sliderAttrs = {
		transitionType: attrs?.transitionType || 'fade',
		transitionSpeed: attrs?.transitionSpeed || 300,
		infinite: attrs?.infinite || true,
		autoplay: attrs?.autoplay !== false && (attrs?.transitionDelay || 3000),
		interactionTypes: ['touch'],
		startIndex: 1,
		columnSlider,
		slideOneColumn: true,
		adaption: columnSlider ? 'columns' : false,
		breakPoint: setup.breakPoint,
	};
	let Wrapper;

	const extraProps = generalSettings({ customProps, attrs, template });

	if (attrs?.freeFlow && attrs?.align === 'full') {
		Wrapper = ({ children }) => (
			<div className={s.container__ccBlocksSlider_inner}>
				<button className={s.container__ccBlocksSlider_scroll} onClick={scrollRight} type="button" aria-label="Scroll Right">
					<svg width="7" height="10" viewBox="0 0 7 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L6 5L1 9" stroke="black" strokeWidth="0.3" /></svg>
					<svg width="12" height="18" viewBox="0 0 12 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L11 9L1 17" stroke="black" strokeWidth="0.3" /></svg>
				</button>
				<div className={s.container__ccBlocksSlider_inner_overflow}>
					{ children }
				</div>
			</div>
		);
	} else {
		Wrapper = ({ children }) => (
			<Slider {...sliderAttrs}>
				{ children }
			</Slider>
		);
	}

	return (
		<div {...extraProps}>
			<Wrapper>
				{
					content.map((childSection) => {
						const tpl = Object.keys(childSection)[0];
						const nextContent = childSection[tpl];
						const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
						if (!ChildElement) {
							console.log('TEMPLATE MISSING FROM: ', nextContent);
							return null;
						}
						return <ChildElement template={tpl} key={nextContent.key} setup={setup} slider {...nextContent} {...props} />;
					})
				}
			</Wrapper>
		</div>
	);
};

containers.coreButtons = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const customProps = {
		className: ['u_buttons'],
	};
	if (!content) return null;
	const isManyButtons = content.filter((tpl) => !isRestricted(setup, tpl[Object.keys(tpl)[0]].attrs));

	const extraProps = generalSettings({ customProps, attrs, template });

	return (
		<div {...extraProps}>
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) {
						console.log('TEMPLATE MISSING FROM: ', nextContent);
						return null;
					}
					return <ChildElement template={tpl} key={nextContent.key} isManyButtons={isManyButtons.length} setup={setup} {...nextContent} {...props} />;
				})
			}
		</div>
	);
};

containers.coreColumn = ({ attrs = {}, content = [], setup, template, column, isFluid, ...props }) => {
	const customProps = {
		className: [],
	};

	if (isFluid) {
		customProps.className = ['u_column', 'u_width__fluid', 'u_width__4'];
	} else if (attrs?.width && attrs.width.indexOf('px') !== -1) {
		customProps.className = ['u_column', 'u_width__set'];
		customProps.style = { width: attrs.width };
	} else if (column) customProps.className = ['u_column', `u_width__${column}`];

	if (!attrs?.width) customProps.className.push('u_width__grow');

	const imageOnlyInColumn = content && content.filter((section) => Object.keys(section).join('') !== 'coreImage').length === 0;
	if (imageOnlyInColumn) customProps.className.push(s.container__imageOnly);

	const extraProps = generalSettings({ customProps, attrs, template });

	return (
		<div {...extraProps}>
			{
				content && content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement || !column) {
						if (!ChildElement) console.log('TEMPLATE MISSING FROM: ', nextContent);
						return null;
					}
					return <ChildElement template={tpl} key={nextContent.key} column={column} setup={setup} {...nextContent} {...props} />;
				})
			}
		</div>
	);
};

containers.coreColumns = ({ attrs = {}, content = [], setup, template, slider = false, ...props }) => {
	const customProps = {};

	const initialColumnWidths = percentToColumn(content);
	const rowOnMobile = attrs.isStackedOnMobile === 0;
	customProps.className = [(rowOnMobile ? s.coreColumns__rowOnMobile : null)];
	const extraProps = generalSettings({ customProps, attrs, template });
	const isFluid = !slider && setup.breakPoint !== 'desktop' && initialColumnWidths.filter((col) => col !== 4).length === 0;

	let Wrapper = ({ children }) => children;

	if (!isFluid) {
		Wrapper = ({ children }) => (
			<div {...extraProps}>
				{ children }
			</div>
		);
	}

	return (
		<Wrapper>
			{
				content.map((childSection, i) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) {
						console.log('TEMPLATE MISSING FROM: ', nextContent);
						return null;
					}
					return <ChildElement template={tpl} key={nextContent.key} column={initialColumnWidths[i]} isFluid={isFluid} setup={setup} {...nextContent} {...props} />;
				})
			}
		</Wrapper>
	);
};

containers.coreListItem = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const customProps = {};

	const extraProps = generalSettings({ setup, customProps, attrs, template });

	return (
		<li {...extraProps}>
			{ attrs?.content?.rawText && <div dangerouslySetInnerHTML={{ __html: purifier(attrs.content.rawText, {}, setup) }} /> }
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
				})
			}
		</li>
	);
};

containers.coreList = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const customProps = {};

	if (!Array.isArray(content)) return <List content={content} />;

	const extraProps = generalSettings({ setup, customProps, attrs, template });
	let Wrapper = ({ children }) => (
		<ul {...extraProps}>
			{ children }
		</ul>
	);

	if (attrs?.ordered) {
		Wrapper = ({ children }) => (
			<ol {...extraProps}>
				{ children }
			</ol>
		);
	}

	return (
		<Wrapper>
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
				})
			}
		</Wrapper>
	);
};

containers.coreGroup = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const customProps = {};

	const extraProps = generalSettings({ customProps, attrs, template });

	return (
		<div {...extraProps}>
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) {
						console.log('TEMPLATE MISSING FROM: ', nextContent);
						return null;
					}
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
				})
			}
		</div>
	);
};

containers.coreMore = () => null;

const NoContainer = ({ attrs = {}, content = [], template, setup, ...props }) => {
	const Template = getTemplate(template, attrs, setup);
	if (!Template) return null;

	let Wrapper = ({ children }) => children;

	// Add wrappers
	if (!props.inline && attrs?.align !== 'full' && template !== 'acfProducts' && template !== 'acfPlaceholder') {
		Wrapper = ({ children }) => (
			<div className={s.container__textPage}>
				<div className={s.container_inner}>{ children }</div>
			</div>
		);
	}

	return (
		<ErrorBoundary message={`CMS TemplateSection: ${template}`} debugData={content}>
			<Wrapper>
				<Template content={content} attrs={attrs} setup={setup} {...props} />
			</Wrapper>
		</ErrorBoundary>
	);
};

const TemplateParser = ({ sections, setup, variantClasses = false, skipFirst = false, ...props }) => {
	if (!sections) return null;
	if (!Array.isArray(sections)) sections = [sections];
	const templateContents = [];
	let first = true;
	let classes = [];
	if (variantClasses) {
		classes = Array.isArray(variantClasses) ? variantClasses : [variantClasses];
		classes = [...classes.filter((c) => !!c).map((c) => s[c])];
	}

	sections.forEach((section, i) => {
		const template = Object.keys(section)[0];
		if (section[template]) {
			const ChildElement = getTemplate(template, section[template].attrs, setup, false);
			if (ChildElement) {
				const Wrapper = ({ children }) => {
					const addProps = {};
					if (classes.length > 0) return <div className={classes.join(' ')}>{ children }</div>;
					if (section[template].anchorLink) addProps.id = section[template].anchorLink;
					if (i !== 0 && Object.keys(sections[i - 1])[0] === 'acfSectionTitle') {
						return (
							<div {...addProps} className={[s.sectionTitle, ...classes].join(' ')}>
								<SectionTitle setup={setup} content={sections[i - 1]} />
								{ children }
							</div>
						);
					}
					if (Object.keys(addProps).length > 0) return <div {...addProps}>{ children }</div>;
					return children;
				};
				const nextContent = section[template];
				templateContents[templateContents.length] = (
					<Wrapper key={nextContent.key}>
						<ChildElement template={template} setup={setup} first={skipFirst ? false : first} last={sections.length - 1 === i} uid={nextContent.key} {...nextContent} {...props} />
					</Wrapper>
				);

				// First section that is visible to customer.
				if (template !== 'coreSpacer') first = false;
			}
		} else {
			console.log('TEMPLATE MISSING FROM: ', section);
		}
	});

	return templateContents;
};

export default hot(module)(TemplateParser);
